/**
 * GoogleAuthPage — hosted on auth.da1timetable.info (central auth domain)
 *
 * Flow:
 * 1. Tenant subdomain redirects here with ?returnTo=https://moeez.da1timetable.info/auth/google-callback
 * 2. This page automatically opens the Google Sign-In popup (One Tap / FedCM) — no button shown.
 * 3. On success, redirects back to tenant with the Google credential (id_token)
 * 4. Tenant then calls backend POST /user/login/google with the credential
 */
import {isAllowedReturnDomain} from 'common/HelperFunctions'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useSearchParams} from 'react-router'

import {toAbsoluteUrl} from '_metronic/helpers'

export function GoogleAuthPage() {
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') || ''
  const [error, setError] = useState('')
  const [redirecting, setRedirecting] = useState(false)
  // True once One Tap / FedCM fails to display (e.g. incognito, no Google session,
  // third-party cookies blocked). We then rely on the rendered button below.
  const [oneTapUnavailable, setOneTapUnavailable] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)

  // Validate returnTo to prevent open redirect
  const isValidReturn = isAllowedReturnDomain(returnTo)

  const handleGoogleCredential = useCallback(
    (credential: string) => {
      if (!isValidReturn) {
        setError('Invalid return URL')
        return
      }

      setRedirecting(true)
      // Redirect back to tenant with the Google credential
      const separator = returnTo.includes('?') ? '&' : '?'
      window.location.replace(`${returnTo}${separator}credential=${encodeURIComponent(credential)}`)
    },
    [returnTo, isValidReturn]
  )

  // Browser tab title for the central auth domain's Google sign-in page.
  useEffect(() => {
    document.title = 'Da1Timetable - Sign in with Google'
  }, [])

  // Initialize Google Sign-In, render the real button, then attempt One Tap.
  // The button is the reliable path (works in incognito / no Google session);
  // One Tap is only a convenience and is frequently suppressed (cooldown after
  // repeated dismissals, blocked third-party cookies, etc).
  useEffect(() => {
    if (!isValidReturn) return

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) {
      setError('Google Sign-In is not configured')
      return
    }

    let cancelled = false
    const start = Date.now()
    let initialized = false

    const renderButton = (google: any) => {
      if (!buttonRef.current) return false
      buttonRef.current.innerHTML = ''
      google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'filled_blue',
        size: 'large',
        shape: 'pill',
        text: 'signin_with',
        width: 280,
      })
      return true
    }

    const tryInit = () => {
      if (cancelled) return
      const google = (window as any).google
      if (google?.accounts?.id) {
        if (!initialized) {
          google.accounts.id.initialize({
            client_id: clientId,
            callback: (response: any) => {
              if (response.credential) {
                handleGoogleCredential(response.credential)
              }
            },
            auto_select: false,
            use_fedcm_for_prompt: true,
            cancel_on_tap_outside: false,
            ux_mode: 'popup',
          })

          // Attempt One Tap / FedCM. If it can't display, surface the button.
          google.accounts.id.prompt((notification: any) => {
            if (notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.()) {
              setOneTapUnavailable(true)
            }
          })
          initialized = true
        }

        // The button container may not be mounted on the first tick; keep
        // retrying until it renders successfully.
        if (renderButton(google)) return
      }
      if (Date.now() - start < 10000) setTimeout(tryInit, 200)
    }

    tryInit()
    return () => {
      cancelled = true
    }
  }, [handleGoogleCredential, isValidReturn])

  const pageBg = {
    background: 'linear-gradient(90.14deg, #431214 0.12%, #031C36 99.87%)',
  }

  if (!isValidReturn) {
    return (
      <div className='d-flex flex-column flex-column-fluid min-vh-100' style={pageBg}>
        <div className='position-absolute top-0 start-0 p-10 d-flex align-items-center gap-2'>
          <img
            src={toAbsoluteUrl('/media/auth/image 70.png')}
            alt='Logo'
            style={{maxHeight: '48px'}}
          />
          <span className='text-white fw-bold fs-4'>Timetable™</span>
        </div>
        <div className='d-flex flex-center flex-column flex-grow-1'>
          <div
            className='text-center px-8 py-10'
            style={{
              background: 'rgba(40, 40, 50, 0.85)',
              backdropFilter: 'blur(12px)',
              borderRadius: 24,
              maxWidth: 420,
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
            }}
          >
            <h4 className='text-danger mb-3'>Invalid Request</h4>
            <p className='text-white-50 mb-0'>The return URL is missing or not allowed.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column flex-column-fluid min-vh-100' style={pageBg}>
      {/* Header Logo */}
      <div className='position-absolute top-0 start-0 p-10 d-flex align-items-center gap-2'>
        <img
          src={toAbsoluteUrl('/media/auth/image 70.png')}
          alt='Logo'
          style={{maxHeight: '48px'}}
        />
        <span className='text-white fw-bold fs-4'>Timetable™</span>
      </div>

      {/* Minimal centered status card — no button, Google popup opens automatically */}
      <div className='d-flex flex-center flex-column flex-grow-1 px-4'>
        <div
          className='text-center px-8 py-10'
          style={{
            background: 'rgba(40, 40, 50, 0.85)',
            backdropFilter: 'blur(12px)',
            borderRadius: 24,
            maxWidth: 380,
            width: '100%',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
          }}
        >
          {/* Google logo */}
          <div className='d-flex justify-content-center mb-5'>
            <svg width='40' height='40' viewBox='0 0 48 48' aria-hidden='true'>
              <path
                fill='#EA4335'
                d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'
              />
              <path
                fill='#4285F4'
                d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'
              />
              <path
                fill='#FBBC05'
                d='M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.09 24.09 0 0 0 0 21.56l7.98-6.19z'
              />
              <path
                fill='#34A853'
                d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'
              />
            </svg>
          </div>

          {error ? (
            <>
              <h5 className='text-white fw-bolder mb-2'>Sign-in unavailable</h5>
              <p className='text-white-50 fs-7 mb-0'>{error}</p>
            </>
          ) : redirecting ? (
            <>
              <div className='d-flex justify-content-center mb-4'>
                <img
                  src={toAbsoluteUrl('/media/logos/LogoAnim1.gif')}
                  alt='Loading'
                  style={{width: 96, height: 96, objectFit: 'contain'}}
                />
              </div>
              <h3 className='text-white fw-bolder mb-3'>Signing you in…</h3>
              <p className='text-white-50 fs-5 mb-0'>
                Please wait while we securely log you into your account.
              </p>
            </>
          ) : (
            <>
              <h5 className='text-white fw-bolder mb-2'>Sign in with Google</h5>
              <p className='text-white-50 fs-7 mb-4'>
                {oneTapUnavailable
                  ? 'Click the button below to choose your Google account.'
                  : 'Choose your account from the Google prompt, or use the button below.'}
              </p>
              {/* Custom dark button is the visual; the real (transparent) Google
                  button is laid on top so the click opens the genuine popup. */}
              <div className='mx-auto' style={{position: 'relative', width: 280, height: 44}}>
                <div
                  className='d-flex align-items-center justify-content-center gap-3 w-100 h-100'
                  aria-hidden='true'
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    borderRadius: 999,
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  <svg width='20' height='20' viewBox='0 0 48 48'>
                    <path
                      fill='#EA4335'
                      d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'
                    />
                    <path
                      fill='#4285F4'
                      d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'
                    />
                    <path
                      fill='#FBBC05'
                      d='M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.09 24.09 0 0 0 0 21.56l7.98-6.19z'
                    />
                    <path
                      fill='#34A853'
                      d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'
                    />
                  </svg>
                  Sign in with Google
                </div>
                {/* Real Google button: transparent overlay, catches the click */}
                <div
                  ref={buttonRef}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    overflow: 'hidden',
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
